import * as stylex from "@stylexjs/stylex";
import { fonts } from "@softmax/design-system/tokens.stylex";
import {
  countLabelOf,
  fillOf,
  glyphKindOf,
  shade,
  type GeneralsBoardFrame,
  type GlyphKind,
  type Tile,
} from "./generals-board-model";

// SVGs avoid relying on terrain glyphs absent from the product's monospace font.
const glyphSvg: Record<GlyphKind, string> = {
  mountain:
    '<svg viewBox="0 0 16 16" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M8 2.3 14.2 13.5 1.8 13.5Z"/></svg>',
  general:
    '<svg viewBox="0 0 16 16" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M8 1.2 9.85 5.8 14.7 6.15 11 9.3 12.1 14.1 8 11.5 3.9 14.1 5 9.3 1.3 6.15 6.15 5.8Z"/></svg>',
  castle:
    '<svg viewBox="0 0 16 16" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M8 1.3 14.3 7h-2.1v7.2h-8.4V7H1.7Z"/></svg>',
};

export function createGeneralsRenderer({
  root,
  size,
}: {
  readonly root: HTMLDivElement;
  readonly size: number;
}) {
  let renderedTiles: {
    readonly tile: Tile;
    readonly el: HTMLDivElement;
    readonly icon: HTMLSpanElement;
    readonly text: HTMLSpanElement;
  }[] = [];
  function build({
    rows: rowCount,
    cols: columnCount,
    tiles,
  }: GeneralsBoardFrame) {
    const tile = Math.floor(size / Math.max(rowCount, columnCount));
    root.style.gridTemplateColumns = `repeat(${columnCount}, ${tile}px)`;
    root.style.gridAutoRows = `${tile}px`;
    root.style.width = `${columnCount * tile}px`;
    root.style.height = `${rowCount * tile}px`;
    root.replaceChildren();
    renderedTiles = tiles.map((sourceTile) => {
      const el = document.createElement("div");
      el.style.width = `${tile}px`;
      el.style.height = `${tile}px`;
      el.className = stylex.props(styles.tile).className ?? "";
      el.style.fontSize = `${Math.max(8, Math.round(tile * 0.46))}px`;

      const icon = document.createElement("span");
      icon.className = stylex.props(styles.icon).className ?? "";
      const text = document.createElement("span");
      el.append(icon, text);
      root.appendChild(el);
      return { tile: sourceTile, el, icon, text };
    });
  }

  function render() {
    for (const tile of renderedTiles) renderTile(tile);
  }

  return { build, render };
}

function renderTile({
  tile: t,
  el,
  icon,
  text,
}: {
  readonly tile: Tile;
  readonly el: HTMLDivElement;
  readonly icon: HTMLSpanElement;
  readonly text: HTMLSpanElement;
}) {
  const { fill, ink } = fillOf(t);
  const kind = glyphKindOf(t);
  const count = countLabelOf(t);

  if (el.dataset.fill !== fill) {
    el.dataset.fill = fill;
    el.style.background = fill;
    el.style.color = ink;
    const rule = shade({ hex: fill, k: 0.72 });
    el.style.borderTop = `1px solid ${rule}`;
    el.style.borderLeft = `1px solid ${rule}`;
  }

  if (icon.dataset.kind !== (kind ?? "")) {
    icon.dataset.kind = kind ?? "";
    icon.innerHTML = kind ? glyphSvg[kind] : "";
    icon.style.display = kind ? "flex" : "none";
  }

  if (text.textContent !== count) text.textContent = count;
}

const styles = stylex.create({
  tile: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    lineHeight: 1,
    fontFamily: fonts.mono,
    fontWeight: 600,
    overflow: "hidden",
    userSelect: "none",
  },
  icon: { display: "flex", width: "1em", height: "1em" },
});
